import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { Mascot } from '@/components/ui/Mascot';
import { isStoreBuild } from '@/lib/platform';
import { LeadModal } from '@/components/LeadModal';
import { useAuthStore } from '@/stores/auth';
import { GuestWordOfTheDay } from './GuestWordOfTheDay';
import { setLanguage } from '@/app/i18n';
import { getLandingBlocks, isFeatureOn } from '@/lib/appConfig';
import { useAppConfig } from '@/hooks/useAppConfig';
import { resolveLandingLayout } from '@/lib/landingLayout';
import { cn } from '@/lib/utils';
import { LegalLink } from '@/components/LegalLink';
import { PRIVACY_URL, TERMS_URL } from '@/lib/legalLinks';
// D76: лендинг в приложении не рисуется, но лежит в том же бандле — бренд тернарником, чтобы «onebilim»
// сайта выпал из dist-mobile (lib/appBrand.ts). На сайте текст прежний.
import { APP_BRAND, IS_APP_BUILD } from '@/lib/appBrand';

/**
 * Лендинг — первый экран и на сайте, и в приложении сразу после установки.
 *
 * ДВЕ ВЕРСИИ ИЗ ОДНОГО КОМПОНЕНТА. Цен нет нигде: правило Apple 3.1.1
 * запрещает уводить из приложения к покупке снаружи, причём под запрет
 * попадает и ссылка на мессенджер, и номер менеджера, и просто текст, зовущий
 * купить вне приложения.
 *
 * ⚠️ 31.08.2026 кнопки в WhatsApp убраны СОВСЕМ, и с сайта тоже: теперь везде
 * форма заявки на пробное занятие. Заявка — просьба связаться, а не покупка,
 * поэтому она доступна и в приложении. Раньше приходилось прятать кнопку, и
 * установивший приложение человек не мог обратиться к школе никак.
 *
 * В приложении по-прежнему остаются описания уровней без цен, а
 * бесплатные материалы поднимаются наверх — чтобы человек, открывший
 * приложение, сразу видел, чем заняться, а не витрину без кнопок.
 *
 * ОФОРМЛЕНИЕ. Шрифт один — Inter, и это не лень: он вшит в сборку локально,
 * потому что приложение обязано открываться без сети, а внешние шрифты
 * запрещены правилами магазинов. Характер набирается не гарнитурой, а
 * крупностью, плотностью и ритмом: заголовок в 5 строк экрана, числа с
 * табличными цифрами, буквы уровней как графика.
 */

/** Регалии преподавателя — лента, которую можно листать. */
const CREDENTIAL_KEYS = ['nu', 'languages', 'celta', 'ielts', 'students'] as const;

/**
 * Три курса школы — ровно те, что в оферте: IELTS, PRE-UPPER, BEGINNER-PRE.
 * Прежняя витрина показывала пять уровней CEFR, которых в договоре нет.
 *
 * ПЛАШКИ — МАКЕТЫ ЗАКАЗЧИКА из `public/courses`, уже без надписи «AUGUST»:
 * витрина висит круглый год, и месяц на ней пришлось бы менять каждые тридцать
 * дней. Присланные 1024×1024 пережаты в WebP шириной 760 px (карточка на
 * десктопе — 372 px, на ретине 744) — 40 КБ на все три вместо 1 МБ.
 *
 * `plate` — цвет фона под картинкой, взят из самого макета пипеткой: пока
 * картинка грузится, на её месте нужный цвет, а не белый провал.
 *
 * `course` уходит в сообщение менеджеру — заглавными, как просил заказчик.
 */
const COURSES = [
  { key: 'ielts', course: 'IELTS', image: 'ielts', plate: 'bg-[#E96E23]' },
  { key: 'preUpper', course: 'PRE-UPPER', image: 'pre-upper', plate: 'bg-[#EDEBEC]' },
  { key: 'beginner', course: 'BEGINNER', image: 'beginner', plate: 'bg-[#334EF4]' },
] as const;

/**
 * Результаты учеников — готовые макеты школы из `public/cases`.
 *
 * ПОЧЕМУ ИМЕНА ЛАТИНИЦЕЙ И В КОДЕ, А НЕ В ПЕРЕВОДАХ: это имена собственные, они
 * не переводятся, и держать их в трёх файлах переводов значит трижды рисковать
 * опечаткой в фамилии живого человека. Латиница — так подписаны и сами макеты
 * школы, и она одинаково читается во всех трёх языках.
 *
 * Переводится только подпись результата: «IELTS Overall 7.0» одинаково всюду,
 * а ЕНТ по-казахски ҰБТ, по-английски UNT — отсюда `exam` и шаблон в переводах.
 *
 * Порядок не по алфавиту: сильный результат идёт первым, слабый последним —
 * ленту листают не до конца.
 */
const STUDENT_CASES = [
  { slug: 'rysbay-kaiyrzhan', name: 'Rysbay Kaiyrzhan', exam: 'ielts', score: '8.0', from: '5.0' },
  { slug: 'zharkynova-dilnaz', name: 'Zharkynova Dilnaz', exam: 'ielts', score: '7.0' },
  { slug: 'nurbayev-zhandos', name: 'Nurbayev Zhandos', exam: 'unt', score: '140' },
  { slug: 'aman-syrym', name: 'Aman Syrym', exam: 'ielts', score: '7.0' },
  { slug: 'kydyrbek-inkar', name: 'Kydyrbek Inkar', exam: 'unt', score: '140' },
  { slug: 'bestan-zhannur', name: 'Bestan Zhannur', exam: 'ielts', score: '7.0' },
  { slug: 'nurlanova-darina', name: 'Nurlanova Darina', exam: 'ielts', score: '7.0' },
  { slug: 'takhirov-nurzhaubay', name: 'Takhirov Nurzhaubay', exam: 'ielts', score: '7.0' },
  { slug: 'duisengali-balzhan', name: 'Duisengali Balzhan', exam: 'ielts', score: '7.0' },
  { slug: 'ukibay-madina', name: 'Ukibay Madina', exam: 'ielts', score: '7.0' },
  { slug: 'rakhymberdi-zhibek', name: 'Rakhymberdi Zhibek', exam: 'ielts', score: '6.0' },
] as const;

const SCHOOL_NUMBERS = ['years', 'students', 'staff'] as const;


/**
 * Языки на первом экране.
 *
 * ЗАЧЕМ ЗДЕСЬ. Платформа открывается на казахском — для наших учеников это
 * верно, но переключатель до сегодня жил только в профиле, то есть ПОСЛЕ входа.
 * Человек (и ревьюер магазина), открывший приложение впервые, видел незнакомый
 * язык и не мог понять, что это за приложение, — а сменить было нечем.
 */
const LANGS = [
  { code: 'kk', label: 'Қаз' },
  { code: 'ru', label: 'Рус' },
  { code: 'en', label: 'Eng' },
] as const;

export function LandingPage() {
  const store = isStoreBuild();
  // Заявка на пробное занятие. null — окно закрыто, строка — о чём заявка
  // (название курса или пусто для общей).
  const [leadAbout, setLeadAboutRaw] = useState<string | null>(null);
  const setLeadAbout = (about: string) => setLeadAboutRaw(about);
  // Язык можно задать адресом: `/?lang=en`. Такую ссылку мы даём ревьюеру
  // магазина и вставляем в рассылки — платформа открывается на казахском, и
  // человек, не читающий по-казахски, иначе видит незнакомый текст и уходит.
  // В отличие от юридических страниц здесь меняем язык интерфейса целиком:
  // это первый экран, дальше человек пойдёт по платформе на выбранном языке.
  const [params] = useSearchParams();
  useEffect(() => {
    const wanted = params.get('lang');
    if (wanted && LANGS.some((l) => l.code === wanted)) setLanguage(wanted as 'kk' | 'ru' | 'en');
  }, [params]);
  const user = useAuthStore((u) => u.user);
  const authed = useAuthStore((u) => Boolean(u.accessToken)) && Boolean(user);
  // ⚠️ Обязательно: настройки приезжают ПОСЛЕ первой отрисовки, и без этой
  // подписки выключатель главной страницы не сработал бы вовсе.
  useAppConfig();

  // В приложении лендинг — это экран ДО входа. Вошедшего ученика он держать не
  // должен: приложение запускается с него каждый раз, и человек, у которого уже
  // есть курс, каждый раз читал бы рекламу вместо своих заданий. В вебе так не
  // делаем: там на лендинг заходят по ссылке и осознанно — из рассылки, из
  // рекламы, поделиться результатами, — и выкидывать человека в кабинет грубо.
  if (store && authed) return <Navigate to={homeFor(user!.role)} replace />;

  // Раскладка с сервера. Не задана или задана бессмыслицей — берётся порядок из
  // сборки: пустым лендинг быть не может, это первый экран приложения.
  const blocks = resolveLandingLayout(getLandingBlocks(), store);

  /**
   * Главная страница сайта выключена тумблером.
   *
   * ⚠️ ТОЛЬКО В ВЕБЕ, И ЭТО ПРИНЦИПИАЛЬНО. В приложении лендинг — первый экран;
   * пустым он быть не может ни при каких настройках, иначе проверяющий магазина
   * видит пустоту и разворачивает по правилу 4.2. Поэтому `!store` в условии.
   *
   * ⚠️ Содержимое НЕ УДАЛЕНО: блоки лежат в сборке, тумблер вернёт их обратно
   * без выката. Заказчик 05.09.2026: «сделай неактивной, но контент не удаляй».
   *
   * Пустую страницу не показываем: человек, пришедший по ссылке, должен понять,
   * куда попал и как войти, — иначе это выглядит как поломка сайта.
   */
  if (!store && !isFeatureOn('landing')) return <LandingPaused />;

  return (
    // overflow-x-CLIP, а не hidden: `hidden` делает этот блок собственной областью
    // прокрутки, и тогда `sticky` у шапки цепляться не за что — она уезжает вверх
    // вместе с содержимым. Именно это и было видно в приложении. `clip` режет
    // горизонтальный вылет так же, но области прокрутки не создаёт.
    <div className="min-h-screen overflow-x-clip bg-[#FFFDFA] text-on-surface">
      <LandingHeader />
      {/* ⚠️ ПОРЯДОК И ВИДИМОСТЬ БЛОКОВ ЗАДАЮТСЯ С СЕРВЕРА — без новой сборки.
          Сами блоки собраны здесь: сервер может переставить и спрятать, но не
          придумать. Подгружать содержимое извне намеренно не делаем — Apple
          разбирает такое по правилам 2.5.2 и 4.7. */}
      {blocks.map((key) => {
        switch (key) {
          case 'hero':        return <Hero key={key} onPick={setLeadAbout} />;
          case 'credentials': return <Credentials key={key} />;
          case 'cases':       return <StudentCases key={key} />;
          case 'courses':     return <Courses key={key} store={store} onPick={setLeadAbout} />;
          case 'free':        return <FreeLayer key={key} store={store} />;
          case 'numbers':     return <SchoolNumbers key={key} />;
          default:            return null;
        }
      })}
      <LandingFooter onPick={setLeadAbout} />

      {/* Заявка на пробное занятие. Доступна и на сайте, и в приложении: это
          услуга с живым человеком, а не покупка. */}
      {leadAbout !== null && (
        <LeadModal about={leadAbout || undefined} onClose={() => setLeadAboutRaw(null)} />
      )}
    </div>
  );
}

/* ─────────────────────────── шапка ─────────────────────────── */

/** Кабинет по роли — та же логика, что на входе. */
function homeFor(role: string): string {
  return role === 'admin' || role === 'manager' ? '/admin' : role === 'curator' ? '/curator' : '/app';
}

function LandingHeader() {
  const { t, i18n } = useTranslation();
  const [solid, setSolid] = useState(false);
  const user = useAuthStore((u) => u.user);
  const authed = useAuthStore((u) => Boolean(u.accessToken)) && Boolean(user);

  // Шапка становится плотной только после прокрутки: поверх героя она должна
  // быть прозрачной, иначе перерезает композицию пополам.
  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={cn(
        'sticky top-0 z-30 transition-colors duration-300',
        solid ? 'border-b border-[#F0E6D8] bg-[#FFFDFA]/90 backdrop-blur' : 'bg-transparent',
      )}
      style={{ paddingTop: 'var(--sa-top)' }}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 md:px-8">
        <span className="text-[22px] font-extrabold tracking-tight text-primary">{IS_APP_BUILD ? APP_BRAND : 'onebilim'}</span>
        <nav className="flex items-center gap-1.5">
          {/* Три языка коротко: на телефоне рядом с кнопкой входа места мало,
              поэтому подписи в три буквы, а не «Қазақша». */}
          <div className="mr-1 flex items-center gap-0.5">
            {LANGS.map(({ code, label }) => (
              <button
                key={code}
                type="button"
                onClick={() => setLanguage(code)}
                aria-current={i18n.language.split('-')[0] === code ? 'true' : undefined}
                className={
                  i18n.language.split('-')[0] === code
                    ? 'rounded-full bg-[#FFF3E3] px-2 py-1 text-[12.5px] font-bold text-primary'
                    : 'rounded-full px-2 py-1 text-[12.5px] font-medium text-[#A99C8C] transition hover:text-[#1A1512]'
                }
              >
                {label}
              </button>
            ))}
          </div>
          {/* Вошедшему предлагать «войти» и «зарегистрироваться» нелепо — ему нужен
              путь к своим занятиям, одной кнопкой. */}
          {authed ? (
            <Link to={homeFor(user!.role)}>
              <Button size="sm">{t('landing.toApp')}</Button>
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className="rounded-pill px-4 py-2 text-sm font-semibold text-on-surface-variant transition hover:bg-[#FFF3E3] hover:text-primary"
              >
                {t('auth.signIn')}
              </Link>
              <Link to="/register">
                <Button size="sm">{t('auth.signUp')}</Button>
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

/* ─────────────────────────── герой ─────────────────────────── */

function Hero({ onPick }: { onPick: (about: string) => void }) {
  const { t } = useTranslation();

  return (
    <section className="relative px-5 pb-12 pt-6 md:px-8 md:pb-20 md:pt-10">
      {/* Тёплые пятна вместо плоской заливки — дают глубину, но не спорят
          с текстом: держатся по краям и сильно размыты. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-[#FFD9A8] opacity-50 blur-3xl" />
        <div className="absolute -right-16 top-32 h-80 w-80 rounded-full bg-[#FFE0CC] opacity-60 blur-3xl" />
      </div>

      <div className="relative mx-auto grid max-w-6xl items-center gap-8 lg:grid-cols-[1.15fr_.85fr]">
        <div>
          <span className="inline-flex items-center gap-2 rounded-pill bg-[#FFF1DE] px-3.5 py-1.5 text-[12.5px] font-bold uppercase tracking-[.08em] text-[#B45309]">
            {t('landing.heroPill')}
          </span>

          <h1 className="mt-5 text-[38px] font-extrabold leading-[1.05] tracking-[-.03em] text-[#1A1512] md:text-[62px]">
            {t('landing.heroTitle')}{' '}
            {/* Подчёркивание — фоном, а не отдельным слоем поверх.
                Сначала это был SVG во всю ширину span: пока заголовок помещался
                в строку, он выглядел правильно, но стоило тексту перенестись —
                и черта растянулась через весь блок, далеко за последнее слово.
                Фон с `box-decoration-break: clone` рисуется под КАЖДОЙ строкой
                ровно по её длине, на любом языке и при любой ширине экрана. */}
            <span
              className="text-primary [box-decoration-break:clone] [-webkit-box-decoration-break:clone]"
              style={{
                backgroundImage: 'linear-gradient(#FFC46B, #FFC46B)',
                backgroundSize: '100% 6px',
                backgroundPosition: '0 100%',
                backgroundRepeat: 'no-repeat',
                paddingBottom: '2px',
              }}
            >
              {t('landing.heroTitleAccent')}
            </span>
          </h1>

          <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-on-surface-variant">
            {t('landing.heroSubtitle')}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/register">
              <Button size="lg">{t('landing.heroCta')}</Button>
            </Link>
            {/* ⚠️ Кнопка видна И В ПРИЛОЖЕНИИ. Раньше она вела в мессенджер, и в
                сборке для магазинов её приходилось прятать: призыв купить мимо
                Apple там запрещён. Из-за этого установивший приложение человек
                не мог обратиться к нам никак. Теперь это заявка на пробное
                занятие — услуга с живым человеком, а не покупка, и прятать её
                не нужно. */}
            {(
              <button
                type="button"
                onClick={() => onPick('')}
                className="inline-flex items-center gap-2 rounded-pill border border-[#E7DCCB] bg-white px-5 py-3 text-sm font-semibold text-on-surface transition hover:border-primary hover:text-primary"
              >
                <span className="material-symbols-outlined text-lg">chat</span>
                {t('landing.heroAskManager')}
              </button>
            )}
          </div>
        </div>

        {/* Маскот. Позы `hello` целые по кадру — фигура не упирается в край,
            поэтому её можно ставить в воздухе, а не только прижатой к низу. */}
        <div className="relative mx-auto w-full max-w-[380px] lg:mx-0">
          <div className="absolute inset-x-6 bottom-6 top-10 rounded-[36px] bg-gradient-to-br from-[#FFB24D] to-[#F5851A] shadow-[0_30px_60px_-30px_rgba(224,123,18,.7)]" />
          <Mascot pose="hello" className="relative z-10 mx-auto h-[300px] w-auto object-contain drop-shadow-2xl md:h-[380px]" />
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── регалии ───────────────────────── */

function Credentials() {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);

  const scrollBy = (dir: 1 | -1) => {
    trackRef.current?.scrollBy({ left: dir * 280, behavior: 'smooth' });
  };

  return (
    <section className="px-5 py-12 md:px-8 md:py-16">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-[26px] font-extrabold tracking-[-.02em] text-[#1A1512] md:text-[34px]">
              {t('landing.aboutTitle')}
            </h2>
            <p className="mt-2 max-w-xl text-[15px] text-on-surface-variant">{t('landing.aboutSubtitle')}</p>
          </div>
          {/* Кнопки прокрутки — для мыши. На телефоне лента листается пальцем. */}
          <div className="hidden gap-2 md:flex">
            {([-1, 1] as const).map((dir) => (
              <button
                key={dir}
                type="button"
                onClick={() => scrollBy(dir)}
                aria-label={t(dir === -1 ? 'landing.scrollPrev' : 'landing.scrollNext')}
                className="grid h-10 w-10 place-items-center rounded-full border border-[#E7DCCB] bg-white text-on-surface-variant transition hover:border-primary hover:text-primary"
              >
                <span className="material-symbols-outlined text-xl">
                  {dir === -1 ? 'chevron_left' : 'chevron_right'}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div
          ref={trackRef}
          className="mt-6 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {CREDENTIAL_KEYS.map((key, i) => (
            <article
              key={key}
              className="min-w-[248px] snap-start rounded-[22px] border border-[#F0E6D8] bg-white p-5 shadow-[0_10px_30px_-24px_rgba(26,21,18,.5)]"
            >
              <span className="text-[13px] font-bold tabular-nums text-[#C9A77A]">
                {String(i + 1).padStart(2, '0')}
              </span>
              <p className="mt-3 text-[17px] font-bold leading-snug text-[#1A1512]">
                {t(`landing.credentials.${key}.title`)}
              </p>
              <p className="mt-1.5 text-[14px] leading-relaxed text-on-surface-variant">
                {t(`landing.credentials.${key}.text`)}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────────────── результаты учеников ──────────────────── */

/**
 * Лента результатов — доказательство между регалиями преподавателя и витриной:
 * человек видит, чем это кончается у учеников, ДО того как ему предложат курс.
 *
 * ВЕС. Исходные макеты школы — PNG по 0,8–1,4 МБ, все одиннадцать дают 12 МБ.
 * В `public/cases` они лежат в WebP шириной 560 px — 236 КБ на весь блок, и это
 * важно вдвойне: папка `public` целиком уезжает и в мобильную сборку, за вес
 * которой мы отдельно воевали. Плюс `loading="lazy"` — при первом открытии
 * страницы картинки не тянутся вообще.
 *
 * ОБРЕЗКА. Макеты пришли в двух пропорциях (0,765 и 0,807). Контейнер 4/5 = 0,8
 * выбран между ними намеренно: у высоких срезается 2% по высоте, у широких 1% по
 * ширине. Любая пропорция «покрасивее» съедала бы у одних низ с баллом, у других
 * края с фамилией.
 *
 * В сборке для магазинов блок остаётся: это результаты, а не продажа, правило
 * Apple 3.1.1 он не задевает.
 *
 * КОЛЬЦО. Список рендерится тремя копиями, лента стартует со средней. Когда
 * человек уезжает в соседнюю копию, `scrollLeft` сдвигается на ширину одной —
 * перескок незаметен, потому что под курсором в этот момент ровно тот же кадр.
 * Границы не существует ни вправо, ни влево, и «конец» никогда не упирается в
 * пустоту.
 *
 * Почему три копии, а не две: со средней копии есть куда листать в обе стороны
 * сразу, без прыжка в первый же момент. Клоны помечены `aria-hidden` — читалка
 * экрана не должна зачитывать одиннадцать учеников трижды.
 *
 * Почему перескок отложен до остановки, а не сразу на границе: на iOS присвоение
 * `scrollLeft` в момент инерционной прокрутки её обрывает, и лента «залипает» под
 * пальцем. Поэтому ждём паузы в событиях прокрутки. Улететь за конец третьей
 * копии одним движением нельзя — это больше четырёх тысяч пикселей.
 */
const CASES_COPIES = [0, 1, 2] as const;
// Три копии витрины — столько же, сколько у кейсов: со средней есть куда листать
// в обе стороны сразу, и шва не видно ни справа, ни слева.
const COURSES_COPIES = [0, 1, 2] as const;

/**
 * Кольцевая лента: долистал до конца — продолжаешь с начала, шва не видно.
 *
 * Как работает: рисуем три копии подряд и стартуем со средней. Когда человек
 * уезжает в крайнюю копию, после паузы в прокрутке сдвигаем ленту ровно на одну
 * копию — кадр под пальцем остаётся тот же, а запас появляется в обе стороны.
 * Пауза (140 мс) нужна, чтобы не дёргать ленту в момент инерции.
 *
 * Один хук на кейсы учеников и на витрину курсов: две копии этой логики
 * разъехались бы, и одна лента однажды перестала бы замыкаться.
 */
function useRingCarousel(copies: number) {
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const copyWidth = () => el.scrollWidth / copies;

    // Ширина карточек фиксирована, поэтому размер ленты не зависит от того,
    // загрузились ли картинки.
    el.scrollLeft = copyWidth();

    let idle: ReturnType<typeof setTimeout>;
    const onScroll = () => {
      clearTimeout(idle);
      idle = setTimeout(() => {
        const width = copyWidth();
        if (width <= 0) return;
        if (el.scrollLeft < width * 0.5) el.scrollLeft += width;
        else if (el.scrollLeft > width * 1.5) el.scrollLeft -= width;
      }, 140);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      clearTimeout(idle);
    };
  }, [copies]);

  const scrollBy = (dir: 1 | -1, step: number) => {
    trackRef.current?.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return { trackRef, scrollBy };
}

function StudentCases() {
  const { t } = useTranslation();
  const { trackRef, scrollBy } = useRingCarousel(CASES_COPIES.length);

  return (
    <section className="px-5 py-12 md:px-8 md:py-16">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-[26px] font-extrabold tracking-[-.02em] text-[#1A1512] md:text-[34px]">
              {t('landing.casesTitle')}
            </h2>
            <p className="mt-2 max-w-xl text-[15px] text-on-surface-variant">{t('landing.casesSubtitle')}</p>
          </div>
          <div className="hidden gap-2 md:flex">
            {([-1, 1] as const).map((dir) => (
              <button
                key={dir}
                type="button"
                onClick={() => scrollBy(dir, 280)}
                aria-label={t(dir === -1 ? 'landing.scrollPrev' : 'landing.scrollNext')}
                className="grid h-10 w-10 place-items-center rounded-full border border-[#E7DCCB] bg-white text-on-surface-variant transition hover:border-primary hover:text-primary"
              >
                <span className="material-symbols-outlined text-xl">
                  {dir === -1 ? 'chevron_left' : 'chevron_right'}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div
          ref={trackRef}
          className="mt-6 flex snap-x gap-4 overflow-x-auto overscroll-x-contain pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {CASES_COPIES.flatMap((copy) =>
            STUDENT_CASES.map((item) => {
              const result = t(`landing.cases.${item.exam}`, { score: item.score });
              return (
                <figure
                  key={`${copy}-${item.slug}`}
                  aria-hidden={copy !== 0}
                  className="min-w-[196px] max-w-[196px] snap-start overflow-hidden rounded-[22px] border border-[#F0E6D8] bg-white shadow-[0_10px_30px_-24px_rgba(26,21,18,.5)] md:min-w-[228px] md:max-w-[228px]"
                >
                  <img
                    src={`/cases/${item.slug}.webp`}
                    alt={t('landing.cases.alt', { name: item.name, result })}
                    loading="lazy"
                    decoding="async"
                    className="aspect-[4/5] w-full object-cover"
                  />
                  <figcaption className="p-4">
                    <p className="text-[15px] font-bold leading-snug text-[#1A1512]">{item.name}</p>
                    <p className="mt-1 text-[13.5px] font-semibold tabular-nums text-primary">{result}</p>
                    {'from' in item && item.from ? (
                      <p className="mt-1 text-[12.5px] text-on-surface-variant">
                        {t('landing.cases.growth', { from: item.from, to: item.score })}
                      </p>
                    ) : null}
                  </figcaption>
                </figure>
              );
            }),
          )}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────── витрина курсов ─────────────────────── */

/**
 * Карточка курса на витрине. Кликается ЦЕЛИКОМ — по просьбе заказчика 21.08.2026:
 * маленькая ссылка внизу карточки не читалась как действие, особенно на телефоне.
 *
 * ⚠️ КУДА ВЕДЁТ — РАЗНОЕ В ВЕБЕ И В ПРИЛОЖЕНИИ, и это не прихоть:
 *
 *  - в вебе карточка открывает форму заявки с названием курса;
 *  - в приложении из магазина — на регистрацию. Ссылка из приложения к покупке
 *    снаружи запрещена правилом Apple 3.1.1 наравне с самой оплатой, и
 *    ловят её именно на таких «безобидных» карточках. Оставлять карточку мёртвой
 *    тоже нельзя — человек тыкает и ничего не происходит, поэтому ведём внутрь.
 */
function CourseCard({
  course,
  store,
  hidden,
  onPick,
}: {
  course: (typeof COURSES)[number];
  store: boolean;
  hidden: boolean;
  onPick: (about: string) => void;
}) {
  const { t } = useTranslation();

  const inner = (
    <>
      {/* Макет курса. Квадрат — как в оригинале, поэтому кадр не режется ни на
          телефоне, ни на десктопе. Цвет фона под картинкой держит место до её
          загрузки. */}
      <img
        src={`/courses/${course.image}.webp`}
        alt={t(`landing.courses.${course.key}.title`)}
        loading="lazy"
        decoding="async"
        className={cn('aspect-square w-full object-cover', course.plate)}
      />
      <div className="flex flex-1 flex-col p-5 text-left">
        <h3 className="text-[19px] font-bold text-[#1A1512]">{t(`landing.courses.${course.key}.title`)}</h3>
        <p className="mt-2 text-[14.5px] leading-relaxed text-on-surface-variant">
          {t(`landing.courses.${course.key}.text`)}
        </p>
        <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-primary transition group-hover:gap-2.5">
          {store ? t('landing.freeCta') : t('landing.levelCta')}
          <span className="material-symbols-outlined text-base">arrow_forward</span>
        </span>
      </div>
    </>
  );

  const shell =
    'group flex min-w-[268px] max-w-[268px] snap-start flex-col overflow-hidden rounded-[24px] border border-[#F0E6D8] bg-white text-left transition hover:-translate-y-0.5 hover:shadow-[0_24px_50px_-32px_rgba(26,21,18,.55)] md:min-w-[300px] md:max-w-[300px]';

  if (store) {
    return (
      <Link to="/register" aria-hidden={hidden} tabIndex={hidden ? -1 : undefined} className={shell}>
        {inner}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onPick(course.course)}
      aria-hidden={hidden}
      tabIndex={hidden ? -1 : undefined}
      className={shell}
    >
      {inner}
    </button>
  );
}

function Courses({ store, onPick }: { store: boolean; onPick: (about: string) => void }) {
  const { t } = useTranslation();
  // Витрина — такая же кольцевая лента, как кейсы. Сеткой три больших макета
  // занимали на телефоне целый экран прокрутки, а в приложении лендинг и без
  // того длинный: первый экран должен доводить до кнопки, а не утомлять.
  const { trackRef, scrollBy } = useRingCarousel(COURSES_COPIES.length);

  return (
    <section className="px-5 py-12 md:px-8 md:py-16">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-[26px] font-extrabold tracking-[-.02em] text-[#1A1512] md:text-[34px]">
              {t('landing.coursesTitle')}
            </h2>
            <p className="mt-2 max-w-2xl text-[15px] text-on-surface-variant">{t('landing.coursesSubtitle')}</p>
          </div>
          {/* Кнопки прокрутки — для мыши. На телефоне лента листается пальцем. */}
          <div className="hidden gap-2 md:flex">
            {([-1, 1] as const).map((dir) => (
              <button
                key={dir}
                type="button"
                onClick={() => scrollBy(dir, 320)}
                aria-label={t(dir === -1 ? 'landing.scrollPrev' : 'landing.scrollNext')}
                className="grid h-10 w-10 place-items-center rounded-full border border-[#E7DCCB] bg-white text-on-surface-variant transition hover:border-primary hover:text-primary"
              >
                <span className="material-symbols-outlined text-xl">
                  {dir === -1 ? 'chevron_left' : 'chevron_right'}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div
          ref={trackRef}
          className="mt-7 flex snap-x gap-4 overflow-x-auto overscroll-x-contain pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {COURSES_COPIES.flatMap((copy) =>
            COURSES.map((course) => (
              <CourseCard
                key={`${copy}-${course.key}`}
                course={course}
                store={store}
                hidden={copy !== 0}
                onPick={onPick}
              />
            )),
          )}
        </div>
      </div>
    </section>
  );
}

function FreeLayer({ store }: { store: boolean }) {
  const { t } = useTranslation();

  return (
    <section className="px-5 py-12 md:px-8 md:py-16">
      <div className="mx-auto max-w-6xl overflow-hidden rounded-[30px] bg-gradient-to-br from-[#1F1A16] to-[#3A2A18] px-6 py-10 text-white md:px-12 md:py-14">
        <div className="grid items-center gap-8 md:grid-cols-[1.2fr_.8fr]">
          <div>
            <span className="inline-flex rounded-pill bg-white/12 px-3.5 py-1.5 text-[12.5px] font-bold uppercase tracking-[.08em] text-[#FFC46B]">
              {t('landing.freePill')}
            </span>
            <h2 className="mt-4 text-[27px] font-extrabold leading-tight tracking-[-.02em] md:text-[36px]">
              {t(store ? 'landing.freeTitleApp' : 'landing.freeTitle')}
            </h2>
            <p className="mt-3 max-w-xl text-[15.5px] leading-relaxed text-white/75">
              {t('landing.freeText')}
            </p>
            <p className="mt-2 text-[13.5px] text-white/55">{t('landing.freeTryNow')}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/register">
                <Button size="lg">{t('landing.freeCta')}</Button>
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center rounded-pill border border-white/25 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                {t('auth.signIn')}
              </Link>
            </div>
          </div>
          {/* Проба без регистрации: настоящее слово дня — то же, что видят
              ученики. Одна кнопка звука, перевод на языке интерфейса. Маскот
              уезжает на второй план: живое задание убеждает лучше картинки. */}
          <div className="space-y-4">
            <GuestWordOfTheDay />
            <Mascot
              pose="books"
              className="mx-auto hidden h-[150px] w-auto object-contain drop-shadow-2xl md:block"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ────────────────────── цифры школы ────────────────────── */

function SchoolNumbers() {
  const { t } = useTranslation();

  return (
    <section className="px-5 py-12 md:px-8 md:py-16">
      <div className="mx-auto grid max-w-6xl gap-4 sm:grid-cols-3">
        {SCHOOL_NUMBERS.map((key) => (
          <div key={key} className="rounded-[22px] border border-[#F0E6D8] bg-white px-6 py-7 text-center">
            <div className="text-[40px] font-extrabold tabular-nums leading-none tracking-[-.03em] text-primary md:text-[48px]">
              {t(`landing.numbers.${key}.value`)}
            </div>
            <div className="mt-2 text-[14px] font-medium text-on-surface-variant">
              {t(`landing.numbers.${key}.label`)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ────────────────────────── футер ────────────────────────── */

function LandingFooter({ onPick }: { onPick: (about: string) => void }) {
  const { t } = useTranslation();

  return (
    <footer
      className="bg-[#1A1512] px-5 py-10 text-[#C9BFB3] md:px-8"
      style={{ paddingBottom: 'calc(2.5rem + var(--sa-bottom))' }}
    >
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 max-w-sm">
          <span className="text-[20px] font-extrabold tracking-tight text-primary">{IS_APP_BUILD ? APP_BRAND : 'onebilim'}</span>
          <p className="mt-2 text-[13.5px] leading-relaxed">
            {t('landing.footerAbout', { brand: IS_APP_BUILD ? APP_BRAND : 'OneBilim' })}
          </p>
        </div>

        {/* Две колонки на телефоне вместо четырёх друг под другом: подвал был
            длиннее самого экрана, и до реквизитов человек просто не доскраливал.
            Контакты и документы короткие — встают рядом; реквизиты длинные, им
            отдана вся ширина. На десктопе колонки выстраиваются в одну строку. */}
        {/* `min-w-0` на колонках обязателен. Без него длинный адрес почты задаёт
            минимальную ширину своей колонке и отжимает соседнюю: на телефоне 360 px
            ссылки на документы ломались на две строки каждая, и колонка выглядела
            рваной. Ширины подобраны замером, а не на глаз: при 12.5 px адресу нужно
            ~155 px, ссылкам на документы ~135 px. Отсюда лёгкий перевес левой колонки
            и узкий зазор — на телефоне 360 px обе строки остаются целыми. */}
        <div className="grid grid-cols-[1.05fr_0.95fr] gap-x-4 gap-y-7 sm:grid-cols-3 lg:grid-cols-4">
          <div className="min-w-0 text-[12.5px] leading-relaxed">
            <p className="font-semibold text-white">{t('landing.footerContactsTitle')}</p>
            <p className="mt-1.5 break-words tracking-tight">{t('landing.footerEmail')}</p>
            <button
              type="button"
              onClick={() => onPick('')}
              className="mt-1 inline-block hover:text-primary"
            >
              {t('lead.title')}
            </button>
          </div>

          {/* Ссылки на документы. Обязательны для магазинов: и Apple, и Google
              проверяют политику открытием ссылки при подаче приложения. */}
          <div className="min-w-0 text-[12.5px] leading-relaxed">
            <p className="font-semibold text-white">{t('landing.footerDocsTitle')}</p>
            <LegalLink url={PRIVACY_URL} className="mt-1.5 block hover:text-primary">
              {t('landing.footerPrivacy')}
            </LegalLink>
            <LegalLink url={TERMS_URL} className="block hover:text-primary">
              {t('landing.footerTerms')}
            </LegalLink>
            {/* Ссылка на оферту убрана 31.08.2026 — решение заказчика: в ней
                порядок оплаты и тарифы. Сам документ доступен только по прямой
                ссылке из договора, в интернете его нет. */}
          </div>

          <div className="col-span-2 text-[13.5px] leading-relaxed sm:col-span-1 lg:col-span-2">
            <p className="font-semibold text-white">{t('landing.footerLegalTitle')}</p>
            <p className="mt-1.5">{t('landing.footerLegalName')}</p>
            <p>{t('landing.footerLegalId')}</p>
            <p className="mt-1">{t('landing.footerLegalAddress')}</p>
          </div>
        </div>

        <p className="mt-8 border-t border-white/10 pt-5 text-[12.5px] text-[#8A7E71]">
          {t('landing.footerRights', { brand: IS_APP_BUILD ? APP_BRAND : 'onebilim' })}
        </p>
      </div>
    </footer>
  );
}

/**
 * Заглушка вместо главной страницы, когда её выключили тумблером.
 *
 * ⚠️ НЕ ПУСТАЯ СТРАНИЦА. Человек, пришедший по ссылке из рассылки, должен
 * понять, куда попал и как войти: белый экран читается как поломка сайта, и
 * звонить по этому поводу будут в школу.
 */
function LandingPaused() {
  const { t } = useTranslation();
  return (
    <div className="grid min-h-screen place-items-center bg-[#FFFDFA] px-6 text-center text-on-surface">
      <div className="max-w-sm">
        <div className="font-display text-3xl font-bold text-primary">{IS_APP_BUILD ? APP_BRAND : 'onebilim'}</div>
        <p className="mt-5 text-sm text-on-surface-variant">{t('landing.pausedNote')}</p>
        <Link
          to="/login"
          className="mt-6 inline-flex items-center justify-center rounded-pill bg-primary px-6 py-3 text-sm font-semibold text-on-primary"
        >
          {t('auth.signIn')}
        </Link>
      </div>
    </div>
  );
}
